import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import axios from 'axios';
import produce from 'immer';
import Select from 'react-select';
import styled from '@emotion/styled';
import TextField from 'components/ui/TextField';
import PlusButton from 'components/new-order-entry/PlusButton';
import { XIconLarge } from 'components/ui/icons';

interface IProps {
  orderId: number;
}

interface IOrderContact {
  id: number;
  orderId: number;
  name: string;
  contactTypeId: number;
  [key: string]: any;
}

interface IContactType {
  id: number;
  type: string;
}

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, max-content);
  gap: 8px 24px;
`;

const selectStyles = {
  container: (provided: any) => ({ ...provided, width: 105 }),
  control: (provided: any) => ({ ...provided, border: '1px solid #e5e5ea', minHeight: 32, height: 32 }),
  menuPortal: (provided: any) => ({ ...provided, zIndex: 10000 }),
  valueContainer: (provided: any) => ({ ...provided, padding: '0 8px' }),
  indicatorsContainer: (provided: any) => ({ ...provided, height: 30 }),
  dropdownIndicator: (provided: any) => ({ ...provided, padding: 4 }),
};

export interface OrderNamesEditHandle {
  save: () => Promise<void>;
}

const OrderNamesEdit = forwardRef<OrderNamesEditHandle, IProps>(function OrderNamesEdit(props, ref) {
  const [contacts, setContacts] = useState<IOrderContact[]>([]);
  const [contactTypes, setContactTypes] = useState<IContactType[]>([]);

  const load = async () => {
    const { data } = await axios.get<IOrderContact[]>(`/api/orders/GetOrderContacts/${props.orderId}`);
    setContacts(data.filter(c => !c.deactivated));
  };

  useEffect(() => {
    axios.get<IContactType[]>('/api/orders/GetOrderContactTypes').then(({ data }) => setContactTypes(data));
  }, []);

  useEffect(() => {
    if (props.orderId) {
      load();
    }
  }, [props.orderId]);

  const typeOptions = contactTypes.map(t => ({ value: t.id, label: t.type }));

  const update = (index: number, changes: Partial<IOrderContact>) => {
    setContacts(
      produce(contacts, draft => {
        Object.assign(draft[index], changes);
      })
    );
  };

  const addName = () => {
    const other = contactTypes.find(t => t.type === 'Other') ?? contactTypes[0];
    setContacts([
      ...contacts,
      {
        id: 0,
        orderId: props.orderId,
        name: '',
        contactTypeId: other?.id,
        checkBankruptcy: false,
        checkPatriot: false,
        checkCogs: false,
        checkFranchise: false,
        checkArticles: false,
        checkTaxWarrant: false,
      },
    ]);
  };

  // Only names that haven't been saved yet can be removed here.
  const removeUnsaved = (index: number) => {
    setContacts(contacts.filter((_, i) => i !== index));
  };

  useImperativeHandle(ref, () => ({
    save: async () => {
      await axios.post(`/api/orders/SaveOrderContactNames/${props.orderId}`, contacts);
    },
  }));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, alignItems: 'flex-start' }}>
      <h3 style={{ fontWeight: 'bold', margin: 0 }}>Order Names</h3>
      <Grid>
      {contacts.map((c, index) => (
        <Row key={c.id || `new-${index}`}>
          <Select
            options={typeOptions}
            value={typeOptions.find(o => o.value === c.contactTypeId) ?? null}
            onChange={(o: any) => update(index, { contactTypeId: o.value })}
            styles={selectStyles}
            menuPortalTarget={document.body}
            menuPosition="fixed"
          />
          <TextField width="230px" height={32} value={c.name || ''} onChange={e => update(index, { name: e.target.value })} />
          {c.id === 0 && (
            <XIconLarge stroke="#e5e5ea" style={{ cursor: 'pointer' }} onClick={() => removeUnsaved(index)} />
          )}
        </Row>
      ))}
      </Grid>
      <Row>
        <PlusButton type="button" buttonText="Add Name" onClick={addName} />
      </Row>
    </div>
  );
});

export default OrderNamesEdit;
